import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, Save, PlugZap, Copy, PhoneCall, FlaskConical, Webhook } from "lucide-react";
import { platformVoiceApi } from "../../lib/api";

const inputCls =
  "w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-[13px] text-slate-200 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/30";

const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/ai-agents/webhooks/elevenlabs`;
const INBOUND_WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/ai-agents/webhooks/elevenlabs/inbound`;

function CopyRow({ value, testid }) {
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 text-[12px] text-cyan-300 font-mono truncate" data-testid={testid}>{value}</code>
      <button
        onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }}
        data-testid={`${testid}-copy`}
        className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-white/10 text-slate-300 hover:bg-white/5 text-[11.5px]"
      >
        <Copy className="h-3 w-3" /> Copy
      </button>
    </div>
  );
}

/** Platform Console → Voice AI tab: ElevenLabs engine credentials + webhook. */
export default function VoiceEngineCard() {
  const [config, setConfig] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [agentId, setAgentId] = useState("");
  const [inboundAgentId, setInboundAgentId] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const c = await platformVoiceApi.config();
      setConfig(c);
      setAgentId(c.agentId || "");
      setInboundAgentId(c.inboundAgentId || "");
      setPhoneNumberId(c.phoneNumberId || "");
    } catch {
      toast.error("Failed to load voice engine config");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const c = await platformVoiceApi.saveConfig({
        agentId,
        inboundAgentId,
        phoneNumberId,
        ...(apiKey ? { apiKey } : {}),
        ...(webhookSecret ? { webhookSecret } : {}),
      });
      setConfig(c);
      setApiKey("");
      setWebhookSecret("");
      toast.success(c.configured ? "Saved — live ElevenLabs calling is ON" : "Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await platformVoiceApi.test();
      setTestResult(res);
      toast.success("ElevenLabs connection verified");
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Connection failed" });
    } finally {
      setTesting(false);
    }
  };

  if (!config) {
    return (
      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5 flex justify-center py-10">
        <Loader2 className="h-5 w-5 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="voice-engine-card">
      <div className="flex items-center gap-2 mb-1">
        <KeyRound className="h-4 w-4 text-cyan-400" />
        <h3 className="text-[14px] font-semibold text-white">ElevenLabs voice engine</h3>
        <span
          data-testid="voice-engine-status"
          className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${
            config.configured
              ? "text-emerald-300 bg-emerald-500/10 border-emerald-500/30"
              : "text-amber-300 bg-amber-500/10 border-amber-500/30"
          }`}
        >
          {config.configured ? <PhoneCall className="h-3 w-3" /> : <FlaskConical className="h-3 w-3" />}
          {config.configured ? "Live · ElevenLabs" : "Simulator mode"}
        </span>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">
        Outbound AI campaign calls switch from the simulator to real ElevenLabs calls the moment all three
        values below are set. Secrets are write-only and encrypted. In your ElevenLabs agent's <b>Security</b> tab,
        enable overrides for <b>prompt, first message, language and voice</b> — each company's scripts and chosen
        voice are applied per call.
      </p>

      <div className="grid md:grid-cols-2 gap-3">
        <Field label="API key">
          <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            placeholder={config.apiKeySet ? "•••••••• (saved)" : "xi-api-key"}
            className={inputCls} data-testid="voice-api-key" />
        </Field>
        <Field label="Outbound Agent ID">
          <input value={agentId} onChange={(e) => setAgentId(e.target.value)}
            placeholder="agent_…" className={inputCls} data-testid="voice-agent-id" />
        </Field>
        <Field label="Inbound Agent ID (receptionist — optional, falls back to outbound)">
          <input value={inboundAgentId} onChange={(e) => setInboundAgentId(e.target.value)}
            placeholder="agent_…" className={inputCls} data-testid="voice-inbound-agent-id" />
        </Field>
        <Field label="Phone Number ID (imported Twilio number)">
          <input value={phoneNumberId} onChange={(e) => setPhoneNumberId(e.target.value)}
            placeholder="phnum_…" className={inputCls} data-testid="voice-phone-number-id" />
        </Field>
        <Field label="Post-call webhook secret">
          <input type="password" value={webhookSecret} onChange={(e) => setWebhookSecret(e.target.value)}
            placeholder={config.webhookSecretSet ? "•••••••• (saved)" : "wsec_… (from ElevenLabs Settings)"}
            className={inputCls} data-testid="voice-webhook-secret" />
        </Field>
      </div>

      <div className="mt-3 rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-2.5">
        <div>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
            <Webhook className="h-3.5 w-3.5" /> Post-call webhook URL — ElevenLabs → Settings → Post-Call Webhook
          </div>
          <CopyRow value={WEBHOOK_URL} testid="voice-webhook-url" />
        </div>
        <div>
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
            <Webhook className="h-3.5 w-3.5" /> Inbound personalization webhook — ElevenLabs → Settings → Conversation Initiation Client Data
          </div>
          <CopyRow value={INBOUND_WEBHOOK_URL} testid="voice-inbound-webhook-url" />
        </div>
        {config.toolSecret && (
          <div>
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
              <KeyRound className="h-3.5 w-3.5" /> Tool secret — add header <code className="text-cyan-300">x-tool-secret</code> on the inbound webhook + every agent tool
            </div>
            <CopyRow value={config.toolSecret} testid="voice-tool-secret" mono />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 mt-4">
        <button onClick={save} disabled={saving} data-testid="voice-engine-save"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-[13px] font-semibold disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
        </button>
        <button onClick={test} disabled={testing} data-testid="voice-engine-test"
          className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-white/10 text-slate-300 hover:bg-white/5 text-[13px] font-medium disabled:opacity-50">
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Test connection
        </button>
      </div>

      {testResult && (
        <div data-testid="voice-test-result"
          className={`mt-3 rounded-lg border p-3 text-[12.5px] ${
            testResult.ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-rose-500/30 bg-rose-500/10 text-rose-200"
          }`}>
          {testResult.ok ? (
            <>
              API key valid{testResult.plan ? <> (<b>{testResult.plan}</b> plan)</> : null}
              {"agentOk" in testResult && (testResult.agentOk
                ? <> · agent <b>{testResult.agent}</b> found</>
                : <> · <span className="text-rose-300">agent ID not found</span></>)}
              {"phoneOk" in testResult && (testResult.phoneOk
                ? <> · number <b>{testResult.phoneNumber}</b> linked</>
                : <> · <span className="text-rose-300">phone number ID not found</span></>)}
              {testResult.live ? " — live calling is ON." : " — add the missing values to go live."}
            </>
          ) : testResult.error}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
